import React, { useEffect, useRef, useState, Suspense } from 'react';
import { ChairModel } from './ChairModel';

interface Particle {
  x: number;
  y: number;
  originX: number;
  originY: number;
  color: string;
  vx: number;
  vy: number;
  size: number;
  ease: number;
}

export const ParticleChair = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<'idle' | 'exploding' | 'model'>('idle');
  
  // Refs for animation state
  const requestRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const processedImageRef = useRef<HTMLCanvasElement | null>(null);
  const isHoveredRef = useRef(false);
  const transitionRef = useRef(0); // 0 = Image, 1 = Particles

  // Configuration
  const GAP = 2; 
  const PARTICLE_SIZE = 1.5;
  const CANVAS_SCALE = 1; 

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Load Image
    const image = new Image();
    image.src = '/assets/ion-source.png'; 
    imageRef.current = image;
    
    image.onload = () => {
      initParticles(image);
      animate();
    };

    const initParticles = (img: HTMLImageElement) => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * CANVAS_SCALE;
      canvas.height = rect.height * CANVAS_SCALE;
      ctx.scale(CANVAS_SCALE, CANVAS_SCALE);

      const offCanvas = document.createElement('canvas');
      offCanvas.width = canvas.width;
      offCanvas.height = canvas.height;
      const offCtx = offCanvas.getContext('2d');
      if (!offCtx) return;

      // Calculate aspect ratio fit
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.85;
      const w = img.width * scale;
      const h = img.height * scale;
      const offsetX = (canvas.width - w) / 2;
      const offsetY = (canvas.height - h) / 2;

      offCtx.drawImage(img, offsetX, offsetY, w, h);
      const imageData = offCtx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      const particles: Particle[] = [];

      // Process image data: Remove white background and generate particles
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i+1];
        const b = data[i+2];
        
        // Check for white background
        if (r > 240 && g > 240 && b > 240) {
          data[i+3] = 0; // Make transparent
        }
      }
      
      // Save processed image for drawing
      offCtx.putImageData(imageData, 0, 0);
      processedImageRef.current = offCanvas;

      // Generate Particles from processed data
      for (let y = 0; y < canvas.height; y += GAP) {
        for (let x = 0; x < canvas.width; x += GAP) {
          const index = (y * canvas.width + x) * 4;
          const alpha = data[index + 3];
          
          if (alpha > 20) { 
            const red = data[index];
            const green = data[index + 1];
            const blue = data[index + 2];
            
            // Calculate brightness
            const brightness = (red + green + blue) / 3;
            
            // Map to Ion Colors
            const r = Math.floor(brightness * 0.2); 
            const g = Math.floor(100 + brightness * 0.6); 
            const b = 255; 
            const a = 0.5 + (brightness / 510); 
            
            particles.push({
              x: x,
              y: y,
              originX: x,
              originY: y,
              color: `rgba(${r}, ${g}, ${b}, ${a})`,
              vx: 0,
              vy: 0,
              size: PARTICLE_SIZE,
              ease: Math.random() * 0.05 + 0.02
            });
          }
        }
      }
      particlesRef.current = particles;
    };

    const animate = () => {
      if (!ctx || !canvas) return;
      
      // Update transition state
      const target = isHoveredRef.current ? 1 : 0;
      // Smooth transition
      transitionRef.current += (target - transitionRef.current) * 0.05;
      const t = transitionRef.current;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // 1. Draw Original Image (Processed) - Fades out on hover
      if (t < 0.99 && processedImageRef.current) {
        ctx.globalAlpha = 1 - t;
        ctx.drawImage(processedImageRef.current, 0, 0);
      }

      // 2. Draw Particles - Fades in on hover
      if (t > 0.01) {
        ctx.globalAlpha = t;
        
        particlesRef.current.forEach(p => {
          if (isHoveredRef.current) {
            // Deconstruct
            const noiseX = (Math.random() - 0.5) * 2;
            const noiseY = (Math.random() - 0.5) * 2;
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const dirX = p.x - centerX;
            const dirY = p.y - centerY;
            const distCenter = Math.sqrt(dirX*dirX + dirY*dirY);
            const expansion = 0.08; 

            p.vx += noiseX * 0.2 + (dirX / (distCenter + 1)) * expansion;
            p.vy += noiseY * 0.2 + (dirY / (distCenter + 1)) * expansion;
            p.vx *= 0.96;
            p.vy *= 0.96;
          } else {
            // Reconstruct
            p.vx += (p.originX - p.x) * p.ease;
            p.vy += (p.originY - p.y) * p.ease;
            p.vx *= 0.90;
            p.vy *= 0.90;
          }

          p.x += p.vx;
          p.y += p.vy;

          ctx.fillStyle = p.color;
          ctx.fillRect(p.x, p.y, p.size, p.size);
        });
      }
      
      ctx.globalAlpha = 1; // Reset alpha
      requestRef.current = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      if (imageRef.current) initParticles(imageRef.current);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    setPhase('exploding');
    // Transition to model after explosion
    setTimeout(() => {
      if (isHoveredRef.current) {
        setPhase('model');
      }
    }, 1500);
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
    setPhase('idle');
  };

  return (
    <div 
      className="w-full h-full relative cursor-pointer group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Layer 1: 3D Model (Bottom) */}
      <div className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${phase !== 'idle' ? 'opacity-100' : 'opacity-0'} ${phase === 'model' ? 'pointer-events-auto' : 'pointer-events-none'}`}>
        <Suspense fallback={null}>
          <ChairModel />
        </Suspense>
      </div>

      {/* Layer 2: Particle/Image Canvas (Top) */}
      <div className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${phase === 'model' ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-none'}`}>
        <canvas 
          ref={canvasRef} 
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
};
